import { JoinResponse, MetaResponse, type JoinResponse as JoinResponseT } from '@gonzovnc/protocol';

/** fetch shape (isomorphic; injectable for tests). */
export type FetchLike = (url: string, init?: {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<{ status: number; ok: boolean; json(): Promise<unknown>; text(): Promise<string> }>;

export class ApiError extends Error {
  constructor(public code: string, public status: number, message: string) {
    super(message);
  }
}

/**
 * Thin client for the GonzoVNC REST API (§3.7). Holds the gonzoToken in memory only
 * (never localStorage/cookies → CSRF structurally absent, §3.7). DOM-free.
 */
export class GonzoApi {
  private token: string | null = null;
  private roomId: string | null = null;

  constructor(private baseUrl: string, private fetchImpl: FetchLike) {}

  get gonzoToken(): string | null { return this.token; }
  get room(): string | null { return this.roomId; }

  private async call<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (auth && this.token) headers.authorization = `Bearer ${this.token}`;
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string } };
    if (!res.ok) {
      const err = data.error;
      throw new ApiError(err?.code ?? 'internal', res.status, err?.message ?? `HTTP ${res.status}`);
    }
    return data as T;
  }

  async meta(): Promise<MetaResponse> {
    return MetaResponse.parse(await this.call('GET', '/api/v1/meta', undefined, false));
  }

  async join(code: string, name: string): Promise<JoinResponseT> {
    const data = await this.call('POST', '/api/v1/rooms/join', { code, name }, false);
    const parsed = JoinResponse.parse(data);
    this.token = parsed.gonzoToken;
    this.roomId = parsed.roomId;
    return parsed;
  }

  async refresh(): Promise<{ lkToken: string; wsUrl: string }> {
    return this.call('POST', `/api/v1/rooms/${this.roomId}/refresh`, {});
  }

  async createCode(role: 'viewer' | 'controller' = 'viewer', ttlSec?: number, maxUses?: number) {
    return this.call<{ codeId: string; code: string; role: string; expiresAt: string }>('POST', `/api/v1/rooms/${this.roomId}/codes`, { role, ttlSec, maxUses });
  }

  async listCodes() {
    return this.call<{ codes: { codeId: string; role: string; expiresAt: string; uses: number; revoked: boolean }[] }>('GET', `/api/v1/rooms/${this.roomId}/codes`);
  }

  async revokeCode(codeId: string) {
    return this.call('DELETE', `/api/v1/rooms/${this.roomId}/codes/${codeId}`);
  }

  async roomState() {
    return this.call<{ state: string; controller: string | null; controlState: string; epoch: number; privateControl: boolean; roster: { identity: string; name: string; role: string; admin: boolean }[] }>('GET', `/api/v1/rooms/${this.roomId}`);
  }

  async grantControl(identity: string, ttlSec?: number) {
    return this.call('POST', `/api/v1/rooms/${this.roomId}/control`, { identity, ttlSec });
  }
  async acceptControl() {
    return this.call<{ holder: string; epoch: number }>('POST', `/api/v1/rooms/${this.roomId}/control/accept`, {});
  }
  async releaseControl() {
    return this.call('POST', `/api/v1/rooms/${this.roomId}/control/release`, {});
  }
  async revokeControl() {
    return this.call('DELETE', `/api/v1/rooms/${this.roomId}/control`);
  }

  async mute(identity: string) { return this.call('POST', `/api/v1/rooms/${this.roomId}/mute`, { identity }); }
  async kick(identity: string, reason?: string) { return this.call('POST', `/api/v1/rooms/${this.roomId}/kick`, { identity, reason }); }
  async setPrivacy(on: boolean) { return this.call('POST', `/api/v1/rooms/${this.roomId}/privacy`, { on }); }

  async postChat(id: string, body: string) { return this.call('POST', `/api/v1/rooms/${this.roomId}/messages`, { id, body }); }
  async getChat(since?: string) {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    return this.call<{ messages: { id: string; name: string; body: string; ts: string }[] }>('GET', `/api/v1/rooms/${this.roomId}/messages${q}`);
  }

  async endRoom() { return this.call('DELETE', `/api/v1/rooms/${this.roomId}`); }
  async telemetry(samples: unknown[]) { return this.call('POST', '/api/v1/telemetry', { samples }); }

  /** For adapters that already have a token (e.g. reconnect); sets the session. */
  adopt(token: string, roomId: string): void {
    this.token = token;
    this.roomId = roomId;
  }
}
